import { useState } from 'react'
import { useOrganization } from '@clerk/clerk-react'
import { toast } from 'sonner'

import {
  REFERRAL_AVAILABILITY_MIN_LOOKBACK_DAYS,
  SYNC_WINDOW_MAX_DAYS,
  SYNC_LOOKBACK_MIN_DAYS,
} from '@/config/constants'
import { titleCase } from '@/lib/utils'

import { ConnectedConnectionSheet } from './ConnectedConnectionSheet'
import { CobaltOnboardingDialog } from './CobaltOnboardingDialog'
import { launchCobaltLink } from './cobaltLink'
import { ConnectionsView } from './ConnectionsView'
import { CredentialIntakeSheet } from './CredentialIntakeSheet'
import { EcwConnectionSheet } from './EcwConnectionSheet'
import { useCredentialIntakeSheet } from './useCredentialIntakeSheet'
import type { CobaltSyncEntity } from './types'
import {
  useCancelCobaltSyncMutation,
  useCobaltConnectTokenMutation,
  useCobaltSyncConfigsQuery,
  useCobaltSyncsQuery,
  useCobaltTokenExchangeMutation,
  useStartCobaltSyncMutation,
  useUpdateCobaltSyncConfigMutation,
} from './useCobaltConnection'
import { useEcwCredentialStatusQuery } from './useCredentialStatusQuery'
import {
  useEcwCredentialPromotionMutation,
  useEcwCredentialValidationMutation,
  useEcwCredentialValidationQuery,
} from './useCredentialValidation'

export function ConnectionsPage() {
  const { organization } = useOrganization()
  const [reviewSheetOpen, setReviewSheetOpen] = useState(false)
  const [connectionSheetOpen, setConnectionSheetOpen] = useState(false)
  const [cobaltOnboardingOpen, setCobaltOnboardingOpen] = useState(false)
  const [isLaunchingCobaltLink, setIsLaunchingCobaltLink] = useState(false)
  const [providerDiscoveryOrgId, setProviderDiscoveryOrgId] = useState<string>()
  const status = useEcwCredentialStatusQuery()
  const cobaltStatus = status.data?.cobalt_status
  const cobaltConnectToken = useCobaltConnectTokenMutation()
  const cobaltTokenExchange = useCobaltTokenExchangeMutation()
  const cobaltSyncConfigs = useCobaltSyncConfigsQuery(
    connectionSheetOpen && cobaltStatus === 'connected'
  )
  // Referral and order availability counts describe their saved lookback
  // windows. A value the endpoint cannot express (a referral 0 saved before
  // the 1-day floor) falls back to the server default. Orders supports 0.
  const savedLookback = (entity: CobaltSyncEntity, minimum: number) => {
    const days = cobaltSyncConfigs.data?.entities.find(
      (config) => config.entity === entity
    )?.lookback_days
    return days != null && days >= minimum && days <= SYNC_WINDOW_MAX_DAYS
      ? days
      : undefined
  }
  const referralLookbackDays = savedLookback(
    'referrals',
    REFERRAL_AVAILABILITY_MIN_LOOKBACK_DAYS
  )
  const orderLookbackDays = savedLookback('orders', SYNC_LOOKBACK_MIN_DAYS)
  // Held until the configs read settles. Firing earlier asks for the default
  // window, then asks again with the saved one — two passes over live Cobalt
  // counts for all entities, with the referral count visibly changing. A
  // failed configs read still releases it, on the server default.
  const cobaltSyncs = useCobaltSyncsQuery(
    connectionSheetOpen &&
      cobaltStatus === 'connected' &&
      !cobaltSyncConfigs.isLoading,
    { referralLookbackDays, orderLookbackDays }
  )
  const startCobaltSync = useStartCobaltSyncMutation()
  const cancelCobaltSync = useCancelCobaltSyncMutation()
  const updateCobaltSyncConfig = useUpdateCobaltSyncConfigMutation()
  const intake = useCredentialIntakeSheet(status.refetch)
  const candidateVersion = status.data?.candidate_version
  const candidateValidation = useEcwCredentialValidationQuery(
    reviewSheetOpen && (status.data?.can_validate_credentials ?? false),
    candidateVersion
  )
  const connectedValidation = useEcwCredentialValidationQuery(
    connectionSheetOpen &&
      status.data?.status === 'configured' &&
      (status.data?.can_review_credentials ?? false),
    status.data?.active_version
  )
  const startValidation = useEcwCredentialValidationMutation(candidateVersion)
  const approveConnection = useEcwCredentialPromotionMutation()

  const validateCredentials = async () => {
    try {
      await startValidation.mutateAsync()
    } catch {
      toast.error('Connection validation could not be started. Try again.')
    }
  }

  const approveCredentials = async () => {
    try {
      await approveConnection.mutateAsync()
      setReviewSheetOpen(false)
      toast.success('Connection approved.')
    } catch {
      toast.error('Connection could not be approved. Refresh and try again.')
    }
  }

  const completeCobaltOnboarding = async (publicToken: string) => {
    try {
      await cobaltTokenExchange.mutateAsync(publicToken)
      await status.refetch()
      setProviderDiscoveryOrgId(organization?.id)
      setConnectionSheetOpen(true)
      toast.success('eClinicalWorks connected.')
    } catch {
      toast.error('Connection could not be completed. Try again.')
    }
  }

  const startCobaltOnboarding = async (emrInstanceDomain?: string) => {
    // Covers the hosted-client load as well as the token mint. The mutation's
    // own isPending clears as soon as the token arrives, which re-enables the
    // button while init.js is still loading — and each extra click mints and
    // burns another one-time link token.
    setIsLaunchingCobaltLink(true)
    try {
      const { link_token } = await cobaltConnectToken.mutateAsync({
        emr_instance_domain: emrInstanceDomain,
      })
      setCobaltOnboardingOpen(false)
      await launchCobaltLink(link_token, completeCobaltOnboarding)
    } catch {
      toast.error('Connection setup could not be opened. Try again.')
    } finally {
      setIsLaunchingCobaltLink(false)
    }
  }

  const openCobaltOnboarding = () => {
    if (cobaltStatus === 'unavailable' || !cobaltStatus) return
    if (cobaltStatus === 'not_connected') {
      setCobaltOnboardingOpen(true)
      return
    }
    void startCobaltOnboarding()
  }

  const startCobaltFullSync = async (
    entity: Parameters<typeof startCobaltSync.mutateAsync>[0]
  ) => {
    try {
      await startCobaltSync.mutateAsync(entity)
      toast.success(`${titleCase(entity)} sync started.`)
    } catch {
      toast.error('Sync could not be started. Try again.')
    }
  }

  const cancelCobaltFullSync = async (
    entity: Parameters<typeof cancelCobaltSync.mutateAsync>[0]
  ) => {
    try {
      await cancelCobaltSync.mutateAsync(entity)
      toast.success('Sync cancelled.')
    } catch {
      toast.error('Sync could not be cancelled. Refresh and try again.')
    }
  }

  // Returns whether the save landed. The caller needs that: an uncontrolled
  // field has to put the stored value back itself when the server rejects one,
  // or the entry stays on screen looking applied.
  const updateCobaltConfig = async (
    entity: Parameters<typeof updateCobaltSyncConfig.mutateAsync>[0]['entity'],
    changes: Parameters<typeof updateCobaltSyncConfig.mutateAsync>[0]['changes']
  ): Promise<boolean> => {
    try {
      await updateCobaltSyncConfig.mutateAsync({ entity, changes })
      toast.success('Sync configuration updated.')
      return true
    } catch {
      toast.error('Sync configuration could not be updated. Try again.')
      return false
    }
  }

  return (
    <>
      <ConnectionsView
        credentialStatus={status.data?.status}
        candidateStatus={status.data?.candidate_status}
        canSubmitCredentials={status.data?.can_submit_credentials ?? false}
        canValidateCredentials={status.data?.can_validate_credentials ?? false}
        isLoading={status.isLoading}
        isError={status.isError}
        isOpening={intake.isLaunching}
        onOpenIntake={intake.openSheet}
        onOpenConnection={() => setConnectionSheetOpen(true)}
        onReview={() => setReviewSheetOpen(true)}
        cobaltStatus={cobaltStatus}
        canStartCobaltOnboarding={
          status.data?.can_start_cobalt_onboarding ?? false
        }
        isRetrying={status.isFetching}
        onRetryCobaltStatus={() => void status.refetch()}
        isCobaltStarting={cobaltConnectToken.isPending || isLaunchingCobaltLink}
        onStartCobaltOnboarding={openCobaltOnboarding}
      />
      <CobaltOnboardingDialog
        open={cobaltOnboardingOpen}
        isStarting={cobaltConnectToken.isPending}
        onOpenChange={setCobaltOnboardingOpen}
        onContinue={(domain) => void startCobaltOnboarding(domain)}
      />
      {(status.data?.status === 'configured' ||
        cobaltStatus === 'connected') && (
        <ConnectedConnectionSheet
          discoverCobaltProviders={
            !!organization?.id && providerDiscoveryOrgId === organization.id
          }
          onProviderDiscoveryStarted={() =>
            setProviderDiscoveryOrgId(undefined)
          }
          credential={status.data}
          validation={connectedValidation.data}
          isValidationLoading={connectedValidation.isLoading}
          isValidationError={connectedValidation.isError}
          cobaltStatus={cobaltStatus}
          isCobaltStatusError={status.isError}
          isCobaltStatusFetching={status.isFetching}
          cobaltStatusError={status.error}
          // Relay gates every Cobalt write on the same roles this flag is
          // computed from, so offering these controls to anyone else only
          // produces a 403 and a "Try again" toast.
          canManageCobaltSyncs={
            status.data?.can_start_cobalt_onboarding ?? false
          }
          isCobaltStarting={
            cobaltConnectToken.isPending || isLaunchingCobaltLink
          }
          cobaltSyncConfigs={cobaltSyncConfigs.data?.entities}
          cobaltSyncEntities={cobaltSyncs.data?.entities}
          isCobaltSyncLoading={cobaltSyncs.isLoading}
          isCobaltSyncError={cobaltSyncs.isError}
          // Reported separately rather than folded into the sync state: a
          // failure here must surface (otherwise the "Scheduled sync" block
          // just vanishes) without also disabling the manual sync controls,
          // which do not depend on this query.
          isCobaltConfigLoading={cobaltSyncConfigs.isLoading}
          isCobaltConfigError={cobaltSyncConfigs.isError}
          startingCobaltSyncEntity={
            startCobaltSync.isPending ? startCobaltSync.variables : undefined
          }
          cancellingCobaltSyncEntity={
            cancelCobaltSync.isPending ? cancelCobaltSync.variables : undefined
          }
          updatingCobaltSyncConfigEntity={
            updateCobaltSyncConfig.isPending
              ? updateCobaltSyncConfig.variables.entity
              : undefined
          }
          open={connectionSheetOpen}
          onOpenChange={setConnectionSheetOpen}
          onOpenIntake={intake.openSheet}
          onRetryValidation={() => void connectedValidation.refetch()}
          onRetryCobaltStatus={() => void status.refetch()}
          onStartCobaltOnboarding={openCobaltOnboarding}
          onStartCobaltSync={(entity) => void startCobaltFullSync(entity)}
          onCancelCobaltSync={(entity) => void cancelCobaltFullSync(entity)}
          onUpdateCobaltSyncConfig={updateCobaltConfig}
        />
      )}
      {status.data?.can_validate_credentials && (
        <EcwConnectionSheet
          credential={status.data}
          validation={candidateValidation.data}
          isLoading={candidateValidation.isLoading}
          isError={candidateValidation.isError}
          isStarting={startValidation.isPending}
          isApproving={approveConnection.isPending}
          open={reviewSheetOpen}
          onOpenChange={setReviewSheetOpen}
          onRetry={() => void candidateValidation.refetch()}
          onValidate={validateCredentials}
          onApprove={approveCredentials}
        />
      )}
      <CredentialIntakeSheet
        open={intake.open}
        targetName={intake.targetName}
        isLaunching={intake.isLaunching}
        iframeRef={intake.iframeRef}
        onOpenChange={intake.onOpenChange}
      />
    </>
  )
}
